import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Workforce, WORKFORCE_STATUSES, BENCH_REASONS, calculateMargin, isEndingSoon, getDaysUntilEnd } from "@/types/workforce";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from "@/components/ui/select";
import {
	User,
	Building2,
	Briefcase,
	Calendar,
	MapPin,
	DollarSign,
	FileText,
	ExternalLink,
	TrendingUp,
	AlertTriangle,
	Phone,
	Mail,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { StickyEditBanner } from "@/components/ui/StickyEditBanner";
import { WorkforceEditTabs } from "./WorkforceEditTabs";

interface WorkforceProfileSectionProps {
	workforce: Workforce;
	isEditing?: boolean;
	editWorkforce?: Workforce;
	onChange?: (field: string, value: any) => void;
	onSave?: () => void;
	onCancel?: () => void;
	onStatusChange?: (status: string) => void;
}

const formatDate = (dateStr: string | null | undefined) => {
	if (!dateStr) return '-';
	try {
		return format(new Date(dateStr), 'MMM d, yyyy');
	} catch {
		return dateStr;
	}
};

const getStatusDotColor = (status: string): string => {
	switch (status) {
		case 'active':
			return 'bg-green-500';
		case 'on_hold':
			return 'bg-yellow-500';
		case 'completed':
			return 'bg-blue-500';
		case 'terminated':
			return 'bg-red-500';
		case 'on_bench':
			return 'bg-orange-400';
		default:
			return 'bg-gray-400';
	}
};

export const WorkforceProfileSection = ({
	workforce,
	isEditing = false,
	editWorkforce,
	onChange,
	onSave,
	onCancel,
	onStatusChange,
}: WorkforceProfileSectionProps) => {
	const { t } = useTranslation();
	const bannerRef = useRef<HTMLDivElement>(null);
	const [isSticky, setIsSticky] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			if (!bannerRef.current) return;
			const rect = bannerRef.current.getBoundingClientRect();
			setIsSticky(rect.top <= 0);
		};
		window.addEventListener("scroll", handleScroll, { passive: true });
		handleScroll();
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	// Use editWorkforce when editing, otherwise use workforce for display
	const displayWorkforce = isEditing && editWorkforce ? editWorkforce : workforce;

	const candidateName = displayWorkforce.candidate?.name || 'Unknown';
	const isBench = !!workforce.is_bench;
	const companyName = workforce.job?.company?.name || workforce.external_company_name || '-';
	const jobTitle = workforce.job?.title || workforce.external_job_title || '-';

	// Calculate margin (only relevant for regular assignments)
	const dailyMargin = calculateMargin(workforce.purchase_price_daily, workforce.selling_price_daily);
	const hourlyMargin = calculateMargin(workforce.purchase_price_hourly, workforce.selling_price_hourly);
	const margin = dailyMargin ?? hourlyMargin;

	// Check if ending soon
	const endDate = workforce.expected_end_date || workforce.end_date;
	const endingSoon = workforce.status === 'active' && isEndingSoon(endDate, 30);
	const daysUntilEnd = getDaysUntilEnd(endDate);
	const isUrgent = daysUntilEnd !== null && daysUntilEnd >= 0 && daysUntilEnd <= 7;

	// Bench reason label
	const benchReasonLabel = workforce.bench_reason
		? BENCH_REASONS.find(r => r.value === workforce.bench_reason)?.label || workforce.bench_reason
		: null;

	return (
		<div className="flex flex-col gap-6">
			{isEditing && onSave && onCancel && (
				<StickyEditBanner
					isSticky={isSticky}
					onSave={onSave}
					onCancel={onCancel}
				>
					{t('common.editMode', 'Edit mode')}
				</StickyEditBanner>
			)}
			<Card className={cn(isEditing && 'ring-2 ring-blue-400 bg-blue-50')}>
			{/* Ending Soon Warning Banner */}
			{!isEditing && endingSoon && (
				<div className="px-6 py-3 flex items-center gap-2 text-sm border-b bg-muted">
					<AlertTriangle className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
					<span className={cn("font-medium", isUrgent && "font-semibold")}>
						{daysUntilEnd === 0
							? t("workforce.endingToday", "Ending today!")
							: daysUntilEnd === 1
								? t("workforce.endingTomorrow", "Ending tomorrow!")
								: t("workforce.endingSoon", "Ending in {{days}} days", { days: daysUntilEnd })}
					</span>
				</div>
			)}

			<CardContent className="p-6">
				{isEditing && editWorkforce && onChange ? (
					<WorkforceEditTabs
						workforce={editWorkforce}
						onChange={onChange}
					/>
				) : (
				<div className="flex flex-col lg:flex-row gap-6">
					{/* Left: Consultant Info */}
					<div className="flex items-start gap-4 lg:w-1/4">
						<Avatar className="h-16 w-16 flex-shrink-0">
							<AvatarFallback className="text-lg bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
								{candidateName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
							</AvatarFallback>
						</Avatar>
						<div className="flex-1 min-w-0">
							<h2 className="text-xl font-semibold truncate">{candidateName}</h2>
							{workforce.candidate && (
								<Link
									to={`/candidates/${workforce.candidate.id}`}
									className="text-sm text-primary hover:underline flex items-center gap-1"
								>
									{t("workforce.viewProfile", "View profile")}
									<ExternalLink className="h-3 w-3" />
								</Link>
							)}
							{workforce.candidate?.email && (
								<div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
									<Mail className="h-3 w-3" />
									<span className="truncate">{workforce.candidate.email}</span>
								</div>
							)}
							{workforce.candidate?.phone && (
								<div className="flex items-center gap-1 text-sm text-muted-foreground">
									<Phone className="h-3 w-3" />
									<span>{workforce.candidate.phone}</span>
								</div>
							)}
						</div>
					</div>

					{/* Center: Assignment Details */}
					<div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
						{isBench ? (
							<>
								{/* Bench: Start Date */}
								<div className="space-y-1">
									<div className="flex items-center gap-2 text-sm text-muted-foreground">
										<Calendar className="h-4 w-4" />
										{t("workforce.bench.startDate", "Bench Start Date")}
									</div>
									<div className="font-medium">
										{formatDate(workforce.start_date)}
									</div>
								</div>

								{/* Bench: Reason */}
								{benchReasonLabel && (
									<div className="space-y-1">
										<div className="flex items-center gap-2 text-sm text-muted-foreground">
											<Briefcase className="h-4 w-4" />
											{t("workforce.bench.reason", "Reason")}
										</div>
										<div className="font-medium">
											{t(`workforce.bench.reasons.${workforce.bench_reason}`, benchReasonLabel)}
										</div>
									</div>
								)}

								{/* Bench: Daily Cost */}
								{workforce.bench_daily_cost != null && (
									<div className="space-y-1">
										<div className="flex items-center gap-2 text-sm text-muted-foreground">
											<DollarSign className="h-4 w-4" />
											{t("workforce.bench.dailyCostLabel", "Daily Cost")}
										</div>
										<div className="font-medium">
											{workforce.currency || 'EUR'} {workforce.bench_daily_cost}/day
										</div>
									</div>
								)}

								{/* Bench: Notes */}
								{workforce.notes && (
									<div className="space-y-1 sm:col-span-2 lg:col-span-3">
										<div className="flex items-center gap-2 text-sm text-muted-foreground">
											<FileText className="h-4 w-4" />
											{t("workforce.fields.notes", "Notes")}
										</div>
										<div className="font-medium text-sm">{workforce.notes}</div>
									</div>
								)}
							</>
						) : (
							<>
								{/* Regular: Company & Role */}
								<div className="space-y-1">
									<div className="flex items-center gap-2 text-sm text-muted-foreground">
										<Building2 className="h-4 w-4" />
										{t("workforce.fields.company", "Company")}
									</div>
									<div className="font-medium">
										{workforce.job?.company ? (
											<Link
												to={`/companies/${workforce.job.company.id}`}
												className="text-primary hover:underline"
											>
												{companyName}
											</Link>
										) : (
											companyName
										)}
									</div>
								</div>

								<div className="space-y-1">
									<div className="flex items-center gap-2 text-sm text-muted-foreground">
										<Briefcase className="h-4 w-4" />
										{t("workforce.fields.role", "Role")}
									</div>
									<div className="font-medium">
										{workforce.job ? (
											<Link
												to={`/jobs/${workforce.job.id}`}
												className="text-primary hover:underline"
											>
												{jobTitle}
											</Link>
										) : (
											jobTitle
										)}
									</div>
								</div>

								{/* Period */}
								<div className="space-y-1">
									<div className="flex items-center gap-2 text-sm text-muted-foreground">
										<Calendar className="h-4 w-4" />
										{t("workforce.fields.period", "Period")}
									</div>
									<div className="font-medium">
										{formatDate(workforce.start_date)}
										<span className="text-muted-foreground"> - </span>
										{workforce.end_date ? formatDate(workforce.end_date) :
										 workforce.expected_end_date ? `${formatDate(workforce.expected_end_date)}` :
										 t("workforce.ongoing", "Ongoing")}
									</div>
								</div>

								{/* Location */}
								{(workforce.location || workforce.location_type) && (
									<div className="space-y-1">
										<div className="flex items-center gap-2 text-sm text-muted-foreground">
											<MapPin className="h-4 w-4" />
											{t("workforce.fields.location", "Location")}
										</div>
										<div className="font-medium flex items-center gap-2">
											{workforce.location || '-'}
											{workforce.location_type && (
												<Badge variant="outline" className="text-xs">
													{workforce.location_type}
												</Badge>
											)}
										</div>
									</div>
								)}

								{/* Contract */}
								{(workforce.contract_type || workforce.contract_reference) && (
									<div className="space-y-1">
										<div className="flex items-center gap-2 text-sm text-muted-foreground">
											<FileText className="h-4 w-4" />
											{t("workforce.fields.contract", "Contract")}
										</div>
										<div className="font-medium flex items-center gap-2">
											{workforce.contract_type && (
												<Badge variant="secondary">
													{workforce.contract_type}
												</Badge>
											)}
											{workforce.contract_reference && (
												<span className="text-sm">{workforce.contract_reference}</span>
											)}
										</div>
									</div>
								)}

								{/* Allocation */}
								{workforce.work_percentage && workforce.work_percentage < 100 && (
									<div className="space-y-1">
										<div className="flex items-center gap-2 text-sm text-muted-foreground">
											<User className="h-4 w-4" />
											{t("workforce.fields.allocation", "Allocation")}
										</div>
										<div className="font-medium">{workforce.work_percentage}%</div>
									</div>
								)}
							</>
						)}
					</div>

					{/* Right: Status & Financial Summary */}
					<div className="lg:w-1/5 lg:border-l lg:pl-6 flex flex-col gap-3">
						<div className="flex items-center">
							<Select
								value={workforce.status}
								onValueChange={(value) => onStatusChange?.(value)}
							>
								<SelectTrigger
									className="h-auto min-w-0 gap-2 border-0 bg-transparent px-0 py-0 text-sm font-normal text-gray-600 shadow-none ring-0 focus:ring-0 focus:ring-offset-0"
								>
									<div className="flex items-center gap-2">
										<div className={`h-2 w-2 rounded-full ${getStatusDotColor(workforce.status)}`} />
										<span className="text-sm font-normal text-gray-600">
											{t(`workforce.statuses.${workforce.status}`, workforce.status)}
										</span>
									</div>
								</SelectTrigger>
								<SelectContent>
									{WORKFORCE_STATUSES.map((status) => (
										<SelectItem key={status.value} value={status.value}>
											<div className="flex items-center gap-2">
												<div className={`h-2 w-2 rounded-full ${getStatusDotColor(status.value)}`} />
												{t(`workforce.statuses.${status.value}`, status.label)}
											</div>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{!isBench && (
							<>
								{/* Margin */}
								{margin !== null && (
									<div className="space-y-1">
										<div className="flex items-center gap-2 text-sm text-muted-foreground">
											<TrendingUp className="h-4 w-4" />
											{t("workforce.fields.margin", "Margin")}
										</div>
										<Badge variant="secondary" className="text-sm">
											{margin.toFixed(1)}%
										</Badge>
									</div>
								)}

								{/* Rate */}
								{(workforce.selling_price_daily || workforce.selling_price_hourly) && (
									<div className="space-y-1">
										<div className="flex items-center gap-2 text-sm text-muted-foreground">
											<DollarSign className="h-4 w-4" />
											{t("workforce.fields.rate", "Rate")}
										</div>
										<div className="font-medium text-sm">
											{workforce.selling_price_daily
												? `${workforce.currency} ${workforce.selling_price_daily}/day`
												: `${workforce.currency} ${workforce.selling_price_hourly}/hour`}
										</div>
									</div>
								)}
							</>
						)}

						{/* Recruiter */}
						{workforce.assigned_recruiter_profile && (
							<div className="space-y-1">
								<div className="flex items-center gap-2 text-sm text-muted-foreground">
									<User className="h-4 w-4" />
									{t("workforce.fields.recruiter", "Recruiter")}
								</div>
								<div className="font-medium text-sm">
									{workforce.assigned_recruiter_profile.name}
								</div>
							</div>
						)}
					</div>
				</div>
				)}
			</CardContent>
		</Card>
		</div>
	);
};
